// ============================================
// COOKIE CONSENT / GOOGLE CONSENT MODE v2
//
// The denied-by-default state and the replay of an already stored decision live
// in public/index.html, above the GTM loader — they must run before gtm.js.
// This module owns what happens once React has booted: reading the stored
// decision, persisting a new one, and pushing `consent update`.
//
// CONSENT_STORAGE_KEY, CONSENT_VERSION and the stored shape are duplicated in
// public/index.html — keep the two in sync.
//
// This module never loads gtag.js, a GA4 property or a second GTM container;
// it only writes consent signals into the existing dataLayer.
// ============================================

export const CONSENT_STORAGE_KEY = "eventio_cookie_consent";
export const CONSENT_VERSION = "1";

/** Dispatched on `window` to reopen the settings dialog (footer "Postavke kolačića") */
export const CONSENT_SETTINGS_EVENT = "eventio:open-cookie-settings";

export interface ConsentDecision {
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

type ConsentSignal = "granted" | "denied";

interface ConsentPayload {
  analytics_storage: ConsentSignal;
  ad_storage: ConsentSignal;
  ad_user_data: ConsentSignal;
  ad_personalization: ConsentSignal;
}

function signal(allowed: boolean): ConsentSignal {
  return allowed ? "granted" : "denied";
}

function buildPayload(analytics: boolean, marketing: boolean): ConsentPayload {
  return {
    analytics_storage: signal(analytics),
    ad_storage: signal(marketing),
    ad_user_data: signal(marketing),
    ad_personalization: signal(marketing),
  };
}

/**
 * Pushes a gtag command onto the dataLayer.
 *
 * GTM only recognises consent commands that arrive as an `arguments` object —
 * `dataLayer.push(["consent", "update", {...}])` is silently ignored — hence the
 * shim rather than a direct push.
 */
function gtagCommand(...args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  const shim = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  (shim as (...commandArgs: unknown[]) => void)(...args);
}

/** Reads the stored decision. Returns null when absent, unreadable or from an older version. */
export function readConsent(): ConsentDecision | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentDecision;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return {
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      timestamp: parsed.timestamp,
      version: parsed.version,
    };
  } catch {
    // Private mode / storage disabled — treat as "no decision yet"
    return null;
  }
}

/** Pushes `consent update` for the given categories without persisting anything. */
export function applyConsent(analytics: boolean, marketing: boolean): void {
  gtagCommand("consent", "update", buildPayload(analytics, marketing));
}

/**
 * Persists the user's decision and pushes the matching `consent update`.
 * Storage failures are non-fatal: the update still reaches the dataLayer, the
 * banner simply reappears on the next visit.
 */
export function saveConsent(
  analytics: boolean,
  marketing: boolean
): ConsentDecision {
  const decision: ConsentDecision = {
    analytics,
    marketing,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(decision));
  } catch {
    // ignore — see doc comment
  }

  applyConsent(analytics, marketing);

  // Extra GTM trigger signal, deliberately pushed AFTER the Consent Mode update
  // so that tags firing on it already see the new consent state. This is not a
  // replacement for Consent Mode, and it is only sent on an explicit user
  // decision — never replayed from the bootstrap in public/index.html on load.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "eventio_consent_update",
    analytics_consent: analytics,
    marketing_consent: marketing,
  });

  return decision;
}

/** Opens the cookie settings dialog from anywhere (used by the footer link). */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(CONSENT_SETTINGS_EVENT));
}
